/**
 * The one place a list number is turned into something a trader reads.
 *
 * In plain terms: the backend stores a plain integer on every bond of a list -
 * order_mst.list_id, the same value on each - and this renders it as LST-000001.
 *
 * ONE definition, used by both the blotter column and the cancel confirmation dialog. Two
 * copies would eventually disagree on the padding and the same list would appear as LST-000042
 * on one screen and LST-42 on the other, which is exactly the kind of thing that makes a trader
 * doubt they are looking at the same enquiry.
 *
 * Named "list order" rather than "list RFQ" to match the backend's list_order_seq: nothing here
 * is protocol-specific, so a CLOB or MarketAxess list would format its number the same way.
 */

/** Shown wherever a value is absent, matching what the blotter already prints for empty cells. */
export const LIST_ID_EMPTY = "---";

const LIST_ID_PREFIX = "LST-";
const LIST_ID_DIGITS = 6;

/**
 * Formats a list number for display.
 *
 * In plain terms: 1 becomes LST-000001, and anything that is not a real number becomes "---".
 *
 * Null is the normal case, not an error: a single-bond RFQ belongs to no list, and so does any
 * order raised before list numbers existed. Both legitimately have nothing to show here.
 *
 * Padded to six digits so the column stays aligned while the numbers grow. A list number past
 * 999999 simply renders with seven digits rather than being truncated - it is an identifier, so
 * showing it wrong would be worse than showing it wide.
 *
 * @param {number|string|null|undefined} listId value from ExtOrderDTO.listId
 * @returns {string} e.g. "LST-000001", or "---" when there is no list
 */
export const formatListId = (listId) => {
  if (listId === null || listId === undefined || listId === "") return LIST_ID_EMPTY;

  // Guard the string case too: the value arrives as JSON, and a number that large could be
  // sent as a string by a future change. Number() on a non-numeric string gives NaN, which
  // would otherwise render as "LST-00NaN".
  const numeric = Number(listId);
  if (!Number.isFinite(numeric)) return LIST_ID_EMPTY;

  return LIST_ID_PREFIX + String(Math.trunc(numeric)).padStart(LIST_ID_DIGITS, "0");
};
